// @flow

import React from 'react';
import {
  Box,
  Button,
  Card,
  Divider,
  Spinner,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import useIntl from '../../hooks/useIntl';
import messages from './messages';
import { useCertsInStage } from './useCertsInStage';
import CertCard from './CertCard';

// The drill-down panel sits to the right of the funnel. Its header names the
// stage the user drilled into and the count of open certs in it; beneath, one
// card per cert.
//
// Step 10 — the panel now follows the funnel's selection and reads the live
// certs endpoint for that stage and property, replacing step 9's static
// fixture. The stage label is passed in (from the stages data, via the funnel
// model) so the header names the stage the moment a bar is clicked, before its
// certs load. The count and the cards come from the certs response.
//
// Every async state is handled here and scoped to the panel, so the funnel
// beside it stays usable no matter what this panel is doing:
//   - loading    — AsyncBox's shared loading indicator (via its `loading`
//                  prop — the prop this library's AsyncBox actually reads)
//   - error      — a clear inline message, rendered here explicitly rather
//                  than delegated to AsyncBox. A failed request must always
//                  surface an error; it must never fall through to an empty
//                  card list, which renders as blank space.
//   - empty      — an explicit "no certs in this stage" message, never a blank
//                  list
//   - pagination — a "load more" control that appends the next page in place,
//                  without reloading the screen
type Props = {
  organizationId: ?string,
  propertyId: ?string,
  stageKey: string,
  stageLabel: string,
};

const CertDrillDownPanel = ({
  organizationId,
  propertyId,
  stageKey,
  stageLabel,
}: Props) => {
  const intl = useIntl();
  const {
    certs,
    totalCount,
    isLoading,
    isError,
    isEmpty,
    hasMore,
    isLoadingMore,
    loadMore,
  } = useCertsInStage({ organizationId, propertyId, stageKey });

  return (
    <Card
      role="region"
      aria-label={intl.formatMessage(messages.panelRegionLabel, {
        label: stageLabel,
      })}
      data-testid="cert-drilldown-panel"
      sx={{
        flex: 1,
        maxWidth: 520,
        minWidth: 280,
        p: 2.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
      }}
    >
      <Stack spacing={2}>
        {/* Header: the stage name (always visible, from the stages data) and,
            once loaded, its count beneath it. The stage name is never a status
            string typed here. Both are forced to block: this library's
            Typography is inline by default, so without it the two run together
            as "Correction Needed9 certifications". */}
        <Box>
          <Typography
            variant="titleSmall"
            sx={{ color: 'text.primary', display: 'block' }}
          >
            {stageLabel}
          </Typography>
          {!isLoading && !isError && (
            <Typography
              variant="bodySmall"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              {intl.formatMessage(messages.panelCount, { count: totalCount })}
            </Typography>
          )}
        </Box>
        <Divider />
        {/* Error is rendered explicitly, not delegated to AsyncBox: a failed
            request must surface a message, never fall through to an empty card
            list (which draws as blank space). Loading and the loaded list stay
            inside AsyncBox, driven by its `loading` prop. */}
        {isError ? (
          <Typography
            role="alert"
            variant="bodyMedium"
            sx={{ color: 'error.main' }}
            data-testid="cert-drilldown-error"
          >
            {intl.formatMessage(messages.panelLoadError)}
          </Typography>
        ) : (
          <AsyncBox loading={isLoading}>
            {isEmpty ? (
              <Typography
                variant="bodyMedium"
                sx={{ color: 'text.secondary' }}
                data-testid="cert-drilldown-empty"
              >
                {intl.formatMessage(messages.panelEmptyState)}
              </Typography>
            ) : (
              <Stack spacing={2}>
                {certs.map((cert) => (
                  <CertCard key={cert.certId} cert={cert} />
                ))}
                {/* More certs than one page: load the next page in place. The
                    shared Spinner shows while the next page is in flight; the
                    already-loaded cards stay on screen the whole time. */}
                {hasMore && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      p: 1,
                    }}
                    data-testid="cert-drilldown-load-more"
                  >
                    {isLoadingMore ? (
                      <Spinner />
                    ) : (
                      <Button
                        variant="text"
                        onClick={() => loadMore()}
                        disabled={isLoadingMore}
                      >
                        {intl.formatMessage(messages.panelLoadMore)}
                      </Button>
                    )}
                  </Box>
                )}
              </Stack>
            )}
          </AsyncBox>
        )}
      </Stack>
    </Card>
  );
};

export default CertDrillDownPanel;
